import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERIES } from "../data/galleries";

const CHAPTER_MEDIA = {
  5: {
    video: "/videos/projecteur-aurora.mp4",
    productSlug: "projecteur-aurora",
    caption: "Le ciel étoilé & l'aurore boréale, chez vous — 3 effets combinables, télécommande incluse.",
    photos: [
      "/images/products/projecteur-chambre.jpg",
      "/images/products/projecteur-salon.jpg",
      "/images/products/projecteur-aurore.jpg",
    ],
  },
};
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import ProductVisual from "./ProductVisual";

const CTA_AMBIANCE = { "projecteur-aurora": "aurora" };

const eur = (n) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

function ProductCard({ product, wide }) {
  const { add, setOpen } = useCart();
  const [size, setSize] = useState(product.sizes ? product.sizes[0] : null);
  const [color, setColor] = useState(product.colors ? product.colors[0] : null);
  const [imgIndex, setImgIndex] = useState(0);
  const [freeBrowse, setFreeBrowse] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const gallery = product.image ? [product.image, ...(GALLERIES[product.slug] || [])] : null;
  const price = size && product.size_prices ? product.size_prices[size] : product.price;
  const colorImage = !freeBrowse && color && product.color_images ? product.color_images[color] : null;
  const shownImage = colorImage || (gallery ? gallery[imgIndex] : null);
  const onColorClick = (c) => {
    setColor(c);
    setFreeBrowse(false);
    const img = product.color_images?.[c];
    const idx = gallery && img ? gallery.indexOf(img) : -1;
    setImgIndex(idx >= 0 ? idx : 0);
  };
  const onThumbClick = (i) => {
    setImgIndex(i);
    if (product.color_images && gallery) {
      const match = Object.keys(product.color_images).find((k) => product.color_images[k] === gallery[i]);
      if (match) {
        setColor(match);
        setFreeBrowse(false);
      } else {
        setFreeBrowse(true);
      }
    }
  };
  const [qty, setQty] = useState(1);
  const onAdd = () => {
    add({ ...product, price }, size, color, qty);
    toast.success(`${qty > 1 ? `${qty} × ` : ""}${product.name}${size ? ` (${size})` : ""}${color ? ` — ${color}` : ""} ajouté au panier`, {
      action: { label: "Voir", onClick: () => setOpen(true) },
    });
  };
  const COLOR_HEX = { Purple: "#9B7EBD", "Bleu profond": "#2E4057", Gris: "#6B7280", Noir: "#1B1714", Beige: "#C4A97C", Rose: "#E6B8AF", "Vert pomme": "#8FBF5F", Violet: "#8E7CC3", "Contour beige — flanelle 7": "#D9C39E", "Contour noir — flanelle 5": "#1B1714", "Contour rouge — flanelle 9": "#A31621", "Colibris — ZJX-FLR-1": "#B7793F", "Mandala — ZJX-FLR-3": "#3B2B24", "Liane — ZJX-FLR-8": "#4E7A5A" };
  return (
    <Reveal className="group scroll-mt-28" id={`fiche-${product.slug}`} data-testid={`product-card-${product.slug}`}>
      <div className={`spotlight overflow-hidden flex items-center justify-center relative ${wide ? "aspect-[16/10]" : "aspect-square"}`}>
        {product.badge && (
          <span data-testid={`badge-${product.slug}`} className="absolute top-4 right-4 z-10 bg-gold text-ivory text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
            {product.badge}
          </span>
        )}
        {product.image ? (
          <div
            className="w-full h-full cursor-zoom-in"
            data-testid={`zoom-${product.slug}`}
            onClick={() => setZoomOpen(true)}
            style={product.zoom && imgIndex === 0 ? { transform: `scale(${product.zoom})` } : undefined}
          >
            <img
              src={shownImage}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          </div>
        ) : (
          <div className="w-2/3 transition-transform duration-700 ease-out group-hover:scale-105">
            <ProductVisual variant={product.visual} />
          </div>
        )}
      </div>
      {gallery && gallery.length > 1 && (
        <div className="mt-3 flex gap-2" data-testid={`gallery-${product.slug}`}>
          {gallery.map((g, i) => (
            <button
              key={g}
              type="button"
              data-testid={`thumb-${product.slug}-${i}`}
              onClick={() => onThumbClick(i)}
              aria-label={`Photo ${i + 1} de ${product.name}`}
              className={`w-16 h-16 overflow-hidden border transition-all duration-300 ${gallery[i] === shownImage ? "border-gold" : "border-choco/15 opacity-60 hover:opacity-100"}`}
            >
              <img src={g} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="pt-6 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl sm:text-2xl font-light text-choco">{product.name}</h3>
          <p className="text-sm text-choco/90 mt-1 font-light">{product.subtitle}</p>
        </div>
        <div className="text-right">
          <p className="font-serif text-lg text-choco whitespace-nowrap" data-testid={`price-${product.slug}`}>{eur(price)}</p>
          <p className="text-[10px] font-light text-choco/75 mt-1 leading-snug" data-testid={`shipping-note-${product.slug}`}>
            <Link to="/livraison-retours" data-testid={`shipping-link-${product.slug}`} className="underline decoration-choco/40 underline-offset-2 hover:text-gold transition-colors duration-300">Frais d'expédition</Link> calculés<br />à l'étape de paiement.
          </p>
        </div>
      </div>
      <p className="mt-3 text-sm font-light leading-relaxed text-choco max-w-md">{product.description}</p>
      <ul className="mt-4 space-y-1">
        {product.details.map((d) => (
          <li key={d} className="text-xs text-choco/90 flex items-center gap-2">
            <span className="text-gold">✦</span> {d}
          </li>
        ))}
      </ul>
      <div className="mt-5" data-testid={`qty-selector-${product.slug}`}>
        <p className="text-xs uppercase tracking-[0.2em] text-choco/90 mb-2">Quantité</p>
        <div className="inline-flex items-center border border-choco/30">
          <button
            type="button"
            data-testid={`qty-minus-${product.slug}`}
            onClick={() => setQty(Math.max(1, qty - 1))}
            aria-label="Diminuer la quantité"
            className="w-9 h-9 flex items-center justify-center text-choco hover:text-gold transition-colors duration-300"
          >
            <Minus size={13} />
          </button>
          <span className="w-10 text-center text-sm" data-testid={`qty-value-${product.slug}`}>{qty}</span>
          <button
            type="button"
            data-testid={`qty-plus-${product.slug}`}
            onClick={() => setQty(Math.min(20, qty + 1))}
            aria-label="Augmenter la quantité"
            className="w-9 h-9 flex items-center justify-center text-choco hover:text-gold transition-colors duration-300"
          >
            <Plus size={13} />
          </button>
        </div>
      </div>
      {product.colors && (
        <div className="mt-5" data-testid={`color-selector-${product.slug}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-choco/90 mb-2">Coloris — <span className="text-gold">{color}</span></p>
          <div className="flex gap-3">
            {product.colors.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`color-${product.slug}-${c}`}
                onClick={() => onColorClick(c)}
                aria-label={`Coloris ${c}`}
                title={c}
                className={`w-10 h-10 rounded-full border-2 shadow-inner transition-all duration-300 ${color === c ? "border-gold scale-110 ring-2 ring-gold/30" : "border-choco/20 hover:border-gold"}`}
                style={{ backgroundColor: COLOR_HEX[c] || "#ccc" }}
              />
            ))}
          </div>
        </div>
      )}
      {product.sizes && (
        <div className="mt-5" data-testid={`size-selector-${product.slug}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-choco/90 mb-2">Choisissez votre taille</p>
          <div className="flex flex-wrap gap-2">
            {product.sizes.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`size-${product.slug}-${s}`}
                onClick={() => setSize(s)}
                className={`px-4 py-2 text-xs border transition-colors duration-300 ${size === s ? "bg-choco text-ivory border-choco" : "border-choco/30 text-choco hover:border-gold hover:text-gold"}`}
              >
                {s}
              </button>
            ))}
          </div>
          {product.size_guide && (
            <p className="mt-3 text-xs text-choco/90 font-light leading-relaxed">{product.size_guide}</p>
          )}
        </div>
      )}
      <button
        data-testid={`add-to-cart-${product.slug}`}
        onClick={onAdd}
        className={`mt-6 text-xs uppercase tracking-[0.25em] px-8 py-3 inline-flex items-center gap-2.5 ${
          CTA_AMBIANCE[product.slug] === "aurora"
            ? "btn-aurora"
            : "border border-choco text-choco hover:bg-choco hover:text-ivory transition-colors duration-400"
        }`}
      >
        {CTA_AMBIANCE[product.slug] === "aurora" && <Sparkles size={13} className="text-[#8FD8CC]" />}
        {product.cta || "Ajouter au panier"}
      </button>
      {zoomOpen && shownImage && createPortal(
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          data-testid={`lightbox-${product.slug}`}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-choco/95 backdrop-blur-sm p-4 sm:p-10 cursor-zoom-out"
          onClick={() => setZoomOpen(false)}
        >
          <img src={shownImage} alt={product.name} className="max-w-full max-h-full object-contain" />
          <button
            type="button"
            data-testid={`lightbox-close-${product.slug}`}
            aria-label="Fermer"
            className="absolute top-5 right-6 text-ivory text-4xl font-light hover:text-gold transition-colors duration-300"
          >
            ×
          </button>
        </motion.div>,
        document.body
      )}
    </Reveal>
  );
}

function ChapterMedia({ chapter, media, items }) {
  const [zoomIndex, setZoomIndex] = useState(null);
  const mediaProduct = media.productSlug ? items.find((p) => p.slug === media.productSlug) : null;
  const count = media.photos.length;
  const prevPhoto = (e) => {
    e.stopPropagation();
    setZoomIndex((i) => (i - 1 + count) % count);
  };
  const nextPhoto = (e) => {
    e.stopPropagation();
    setZoomIndex((i) => (i + 1) % count);
  };
  return (
    <Reveal className="mt-10 md:mt-14">
      <div data-testid={`chapter-media-0${chapter}`}>
        <video
          data-testid={`chapter-video-0${chapter}`}
          src={media.video}
          autoPlay
          muted
          loop
          playsInline
          controls
          className="w-full max-h-[70vh] object-contain bg-choco"
        />
        {mediaProduct && (
          <div
            className="mt-5 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-choco/15 pb-5"
            data-testid={`chapter-media-info-0${chapter}`}
          >
            <div>
              <p className="font-serif text-2xl sm:text-3xl font-light text-choco" data-testid={`media-name-${mediaProduct.slug}`}>{mediaProduct.name}</p>
              <p className="font-accent italic text-base text-choco/85 mt-1" data-testid={`media-caption-${mediaProduct.slug}`}>{media.caption}</p>
            </div>
            <div className="flex items-baseline gap-5">
              <p className="font-serif text-2xl text-gold" data-testid={`media-price-${mediaProduct.slug}`}>{eur(mediaProduct.price)}</p>
              <a
                href={`#fiche-${mediaProduct.slug}`}
                data-testid={`media-link-${mediaProduct.slug}`}
                className="text-xs uppercase tracking-[0.25em] text-choco underline decoration-gold/60 underline-offset-4 hover:text-gold transition-colors duration-300"
              >
                Voir la fiche ↓
              </a>
            </div>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 md:gap-4 mt-2 md:mt-4">
          {media.photos.map((src, i) => (
            <button
              key={src}
              type="button"
              data-testid={`chapter-photo-0${chapter}-${i}`}
              onClick={() => setZoomIndex(i)}
              aria-label={`Agrandir la photo ${i + 1}`}
              className="overflow-hidden cursor-zoom-in group/photo"
            >
              <img
                src={src}
                alt="Projecteur Aurora en situation"
                loading="lazy"
                className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover/photo:scale-105"
              />
            </button>
          ))}
        </div>
        {zoomIndex !== null && createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            data-testid={`chapter-lightbox-0${chapter}`}
            className="fixed inset-0 z-[999] flex items-center justify-center bg-choco/95 backdrop-blur-sm p-4 sm:p-10 cursor-zoom-out"
            onClick={() => setZoomIndex(null)}
          >
            <img
              src={media.photos[zoomIndex]}
              alt="Projecteur Aurora en situation"
              className="max-w-full max-h-full object-contain cursor-default"
              onClick={(e) => e.stopPropagation()}
            />
            {count > 1 && (
              <>
                <button
                  type="button"
                  data-testid={`chapter-lightbox-prev-0${chapter}`}
                  aria-label="Photo précédente"
                  onClick={prevPhoto}
                  className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 text-ivory/80 hover:text-gold transition-colors duration-300"
                >
                  <ChevronLeft size={42} strokeWidth={1.25} />
                </button>
                <button
                  type="button"
                  data-testid={`chapter-lightbox-next-0${chapter}`}
                  aria-label="Photo suivante"
                  onClick={nextPhoto}
                  className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 text-ivory/80 hover:text-gold transition-colors duration-300"
                >
                  <ChevronRight size={42} strokeWidth={1.25} />
                </button>
                <span
                  data-testid={`chapter-lightbox-counter-0${chapter}`}
                  className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ivory/80 text-xs tracking-[0.35em]"
                >
                  {zoomIndex + 1} / {count}
                </span>
              </>
            )}
            <button
              type="button"
              data-testid={`chapter-lightbox-close-0${chapter}`}
              aria-label="Fermer"
              className="absolute top-5 right-6 text-ivory text-4xl font-light hover:text-gold transition-colors duration-300"
            >
              ×
            </button>
          </motion.div>,
          document.body
        )}
      </div>
    </Reveal>
  );
}

export default function Chapters({ products }) {
  const chapterNums = [...new Set(products.map((p) => p.chapter))].sort((a, b) => a - b);
  const chapters = chapterNums.map((n) => products.filter((p) => p.chapter === n)).filter((c) => c.length);
  return (
    <section id="univers" data-testid="products-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36 space-y-28 md:space-y-40">
      {chapters.map((items, idx) => {
        const { chapter, chapter_title, chapter_note } = items[0];
        const left = idx % 2 === 0;
        return (
          <div key={chapter} id={`chapitre-0${chapter}`} data-testid={`chapter-0${chapter}`} className="scroll-mt-24">
            <Reveal className={`flex items-baseline gap-6 ${left ? "" : "md:justify-end"}`}>
              <span className="font-serif text-gold text-4xl md:text-6xl font-light">0{chapter}</span>
              <div className={left ? "" : "md:text-right"}>
                <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-choco">{chapter_title}</h2>
                <p className="font-accent italic text-lg text-choco/90 mt-1">{chapter_note}</p>
              </div>
            </Reveal>
            {CHAPTER_MEDIA[chapter] && (
              <ChapterMedia chapter={chapter} media={CHAPTER_MEDIA[chapter]} items={items} />
            )}
            {items.length === 1 ? (
              <div className={`mt-12 grid md:grid-cols-2 gap-14 md:gap-20 items-start ${left ? "" : "md:[direction:rtl]"}`}>
                <div className="md:[direction:ltr]"><ProductCard product={items[0]} wide={false} /></div>
                <div className="hidden md:block md:[direction:ltr] pt-24">
                  <p className="font-accent italic text-2xl text-choco/85 leading-relaxed">
                    « Des matières honnêtes, des gestes simples, un quotidien plus doux. »
                  </p>
                  <p className="mt-4 text-xs uppercase tracking-[0.3em] text-gold">— Karyna</p>
                </div>
              </div>
            ) : (
              <div className="mt-12 grid gap-14 md:gap-20 md:grid-cols-2">
                {items.map((p) => (
                  <ProductCard key={p.slug} product={p} wide={false} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
